/* eslint-disable jsx-a11y/no-autofocus */
"use client";

import { cn } from "@resume-studio/utils";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  EditorState,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode, $createHeadingNode } from "@lexical/rich-text";
import { LinkNode } from "@lexical/link";
import { CodeNode } from "@lexical/code";

import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Underline as UnderlineIcon,
} from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Toggle } from "./toggle";
import { TooltipWrapper } from "./tooltip-wrapper";

// Mobile detection utility
const isMobile = () => {
  return (
    typeof window !== "undefined" &&
    (window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      "ontouchstart" in window ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 1))
  );
};

// Theme configuration
const theme = {
  ltr: "ltr",
  rtl: "rtl",
  paragraph: "mb-1",
  heading: {
    h1: "text-2xl font-bold mb-2 mt-4",
    h2: "text-xl font-bold mb-2 mt-3",
    h3: "text-lg font-bold mb-1 mt-2",
  },
  list: {
    // ol: "list-decimal list-inside mb-1",
    // ul: "list-disc list-inside mb-1",
    listitem: "mb-0.5",
  },
  text: {
    bold: "font-bold",
    italic: "italic",
    underline: "underline",
  },
  code: "bg-muted px-2 py-1 rounded font-mono text-sm",
};

// Simple toolbar component
const Toolbar = () => {
  const [editor] = useLexicalComposerContext();

  const formatText = (format: "bold" | "italic" | "underline") => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatBulletList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const element =
          anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();

        if ($isListNode(element) && element.getListType() === "bullet") {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
      }
    });
  };

  const formatNumberedList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const element =
          anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();

        if ($isListNode(element) && element.getListType() === "number") {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        }
      }
    });
  };

  const formatHeading = (headingSize: "h1" | "h2" | "h3") => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const headingNode = $createHeadingNode(headingSize);
        selection.insertNodes([headingNode]);
      }
    });
  };

  const formatCode = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border p-1 sm:p-2">
      <TooltipWrapper content="Bold">
        <Toggle size="sm" onPressedChange={() => formatText("bold")}>
          <Bold className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Italic">
        <Toggle size="sm" onPressedChange={() => formatText("italic")}>
          <Italic className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Underline">
        <Toggle size="sm" onPressedChange={() => formatText("underline")}>
          <UnderlineIcon className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Heading 1">
        <Toggle size="sm" onPressedChange={() => formatHeading("h1")}>
          <Heading1 className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Heading 2">
        <Toggle size="sm" onPressedChange={() => formatHeading("h2")}>
          <Heading2 className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Heading 3">
        <Toggle size="sm" onPressedChange={() => formatHeading("h3")}>
          <Heading3 className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Bullet List">
        <Toggle size="sm" onPressedChange={formatBulletList}>
          <List className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Numbered List">
        <Toggle size="sm" onPressedChange={formatNumberedList}>
          <ListOrdered className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Code">
        <Toggle size="sm" onPressedChange={formatCode}>
          <Code className="size-4" />
        </Toggle>
      </TooltipWrapper>
    </div>
  );
};

// Lexical Editor wrapper with TipTap-style API
class LexicalEditorWrapper {
  constructor(private editor: any) {}

  // TipTap-compatible commands
  commands = {
    setContent: (content: string) => {
      this.editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(content, "text/html");
        const nodes = $generateNodesFromDOM(this.editor, dom);

        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    },
  };

  // TipTap-compatible text getter
  getText(): string {
    return this.editor.getEditorState().read(() => {
      const root = $getRoot();
      return root.getTextContent();
    });
  }

  // Get HTML content
  getHTML(): string {
    return this.editor.getEditorState().read(() => {
      return $generateHtmlFromNodes(this.editor, null);
    });
  }
}

// Footer plugin that provides editor instance to footer function
function FooterPlugin({ footer }: { footer?: (editor: LexicalEditorWrapper) => React.ReactNode }) {
  const [editor] = useLexicalComposerContext();

  if (!footer) return null;

  const wrappedEditor = new LexicalEditorWrapper(editor);
  return <>{footer(wrappedEditor)}</>;
}

// Content management plugin - simplified for initial content only
function ContentManagerPlugin({
  content,
  onChange,
}: {
  content: string;
  onChange: (html: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  // Guard to prevent onChange from firing before initial content is set
  const isInitializedRef = useRef(false);

  // Set initial content only once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (content) {
      // Wrap plain text in paragraph tags if not HTML
      const htmlContent = content.startsWith("<") ? content : `<p>${content}</p>`;
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlContent, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);

        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    }
    // Mark as initialized after a microtask to ensure editor.update() has completed
    queueMicrotask(() => {
      isInitializedRef.current = true;
    });
  }, []); // run once to avoid resetting user edits on prop changes

  // Sync editor when external content prop changes
  useEffect(() => {
    const nextHtml = content ?? "";
    let currentHtml = "";
    try {
      currentHtml = editor.getEditorState().read(() => {
        return $generateHtmlFromNodes(editor, null);
      });
    } catch {
      // noop
    }

    if (currentHtml !== nextHtml) {
      // Wrap plain text in paragraph tags if not HTML
      const htmlContent = nextHtml.startsWith("<") ? nextHtml : `<p>${nextHtml}</p>`;
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlContent, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);

        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    }
  }, [editor, content]);

  // Immediate onChange for responsive preview; server saves are already debounced in store
  const handleOnChange = useCallback(
    (editorState: EditorState) => {
      // Skip onChange during initialization to prevent empty content from being written to store
      if (!isInitializedRef.current) {
        return;
      }
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        onChange(htmlString);
      });
    },
    [editor, onChange],
  );

  return <OnChangePlugin onChange={handleOnChange} />;
}

function FocusManagementPlugin({ autoFocus }: { autoFocus: boolean }) {
  const [editor] = useLexicalComposerContext();
  const blockInitialFocusRef = useRef(!autoFocus);

  useEffect(() => {
    blockInitialFocusRef.current = !autoFocus;
  }, [autoFocus]);

  useEffect(() => {
    if (autoFocus) {
      const frameId = requestAnimationFrame(() => {
        editor.focus();
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    }

    const unregister = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        if (blockInitialFocusRef.current) {
          blockInitialFocusRef.current = false;
          const root = editor.getRootElement();
          root?.blur();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    return unregister;
  }, [autoFocus, editor]);

  useEffect(() => {
    if (autoFocus) return;

    const root = editor.getRootElement();
    if (!root) return;

    const allowFocus = () => {
      blockInitialFocusRef.current = false;
    };

    root.addEventListener("pointerdown", allowFocus);
    root.addEventListener("keydown", allowFocus);

    return () => {
      root.removeEventListener("pointerdown", allowFocus);
      root.removeEventListener("keydown", allowFocus);
    };
  }, [autoFocus, editor]);

  return null;
}

interface FloatingAIProps {
  // Accept a React node or a render function that receives the editor wrapper
  button: React.ReactNode | ((editor: LexicalEditorWrapper) => React.ReactNode);
}

export interface LexicalInputProps {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  hideToolbar?: boolean;
  className?: string;
  editorClassName?: string;
  header?: React.ReactNode;
  footer?: (editor: LexicalEditorWrapper) => React.ReactNode;
  floatingAI?: FloatingAIProps;
  autoFocus?: boolean;
  instanceId?: string;
}

export const LexicalInput = forwardRef<HTMLDivElement, LexicalInputProps>(
  (
    {
      content = "",
      onChange,
      placeholder = "Start typing...",
      hideToolbar = false,
      className,
      editorClassName = "min-h-[300px]",
      header,
      footer,
      floatingAI,
      autoFocus,
      instanceId,
    },
    _ref,
  ) => {
    const reactId = useId();
    const editorNamespace = useMemo(() => {
      return `LexicalInput-${instanceId ?? reactId}`;
    }, [instanceId, reactId]);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(_ref, () => containerRef.current as HTMLDivElement, []);

    const initialConfig = useMemo(
      () => ({
        namespace: editorNamespace,
        theme,
        onError: (error: Error) => {
          console.error("Lexical error:", error);
        },
        nodes: [HeadingNode, ListNode, ListItemNode, LinkNode, CodeNode],
      }),
      [editorNamespace],
    );

    const handleChange = useCallback(
      (html: string) => {
        onChange?.(html);
      },
      [onChange],
    );

    // Determine if we should auto-focus based on mobile detection
    const shouldAutoFocus = autoFocus !== undefined ? autoFocus : false;

    // Handle focus event for mobile scroll behavior
    const handleFocus = useCallback(() => {
      if (isMobile()) {
        // Small delay to ensure keyboard is showing
        setTimeout(() => {
          containerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
      }
    }, []);

    return (
      <div ref={containerRef} className={cn("relative", className)}>
        <LexicalComposer key={editorNamespace} initialConfig={initialConfig}>
          {header && (
            <div className="rounded-t border-x border-t border-border bg-background">{header}</div>
          )}

          {!hideToolbar && <Toolbar />}

          <div className="relative">
            <RichTextPlugin
              contentEditable={
                <div className="relative">
                  <ContentEditable
                    className={cn(
                      "prose prose-sm max-h-[200px] w-full max-w-none cursor-text overflow-y-auto border border-border bg-secondary px-2 py-1 text-sm placeholder:opacity-80 hover:cursor-text focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[300px] sm:px-3 sm:py-2 [&_*]:my-1",
                      hideToolbar && "pt-2",
                      header && "rounded-t-none border-t-0",
                      floatingAI && "pb-12 pr-14", // Add padding to prevent content overlap with floating AI
                      editorClassName,
                    )}
                    style={{
                      caretColor: "hsl(var(--primary))", // Use primary color for better visibility
                    }}
                    aria-label="Rich text editor"
                    onFocus={handleFocus}
                  />
                  {floatingAI && <FloatingAIContainer render={floatingAI.button} />}
                </div>
              }
              placeholder={
                <div className="pointer-events-none absolute left-2 top-1 text-sm text-muted-foreground sm:left-3 sm:top-2">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />

            <ContentManagerPlugin content={content} onChange={handleChange} />
            <FocusManagementPlugin autoFocus={shouldAutoFocus} />
            <HistoryPlugin />
            <ListPlugin />

            {footer && (
              <div className="border-t border-border bg-background">
                <FooterPlugin footer={footer} />
              </div>
            )}
          </div>
        </LexicalComposer>
      </div>
    );
  },
);

LexicalInput.displayName = "LexicalInput";

// Internal component that renders the floating AI element with access to the editor
function FloatingAIContainer({
  render,
}: {
  render: React.ReactNode | ((editor: LexicalEditorWrapper) => React.ReactNode);
}) {
  const [editor] = useLexicalComposerContext();
  const wrappedEditor = useMemo(() => new LexicalEditorWrapper(editor), [editor]);

  const content = useMemo(() => {
    return typeof render === "function" ? (render as any)(wrappedEditor) : render;
  }, [render, wrappedEditor]);

  return (
    <div className="pointer-events-auto absolute bottom-3 right-3 z-10">
      <div className="rounded border bg-background/95 shadow-sm backdrop-blur-sm">{content}</div>
    </div>
  );
}
